/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import jsPDF from 'jspdf';
import {
  EscalationOption,
  Incident,
  initialBasicInfoType,
  Option,
  ReferenceKeys,
} from '../../types/types';
import { fetchDMList, getTicketInfo } from '../../api/user';

export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const gridStyle = { paddingLeft: '50px', paddingRight: '50px' };

export const cleanIncidentData = (sectionData: any) => {
  const cleaned: any = {};

  Object.entries(sectionData).forEach(([key, value]) => {
    if (
      value !== null &&
      value !== undefined &&
      !(Array.isArray(value) && value.length === 0) &&
      !(typeof value === 'object' && Object.keys(value).length === 0)
    ) {
      cleaned[key] = value;
    }
  });

  return cleaned;
};

export const unformatPhone = (formattedPhone: string): string => {
  return formattedPhone?.replace(/\D/g, '');
};

export const prepareApiPayload = (
  incident: initialBasicInfoType,
  user: any
) => {
  console.log('prepareApiPayload', incident);
  const cleaned = cleanIncidentData(incident);
  const basic = cleaned ?? {};

  const payload: any = {
    shortDescription: basic.shortDescription,
    description: basic.description,
    rentalReferenceNumber: basic.agreementnumber,
    storeNumber: basic.storenumber,
    openBy: user?.employeeId ?? '',
    openByName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
    openByRole: user?.role ?? '',
    status: 'NEW',
    requestType: basic.request_type,
    channel: basic.channel,
    category: basic.category,
    subCategory: basic.sub_category,
    actionType: basic.action_type,
    language: basic.language,
    contactInfo: {
      firstName: basic.firstName,
      lastName: basic.lastName,
      primaryPhoneNumber: unformatPhone(basic.callback),
      alternatePhoneNumber: unformatPhone(basic.alterphonenumb),
    },
    attachments:
      basic.attachments?.map(
        (att: { attachmentName: any; attachmentLocation: any }) => ({
          documentName: att.attachmentName,
          documentLocation: att.attachmentLocation,
        })
      ) ?? [],
  };

  if (basic.assignment?.assignmentGroup) {
    payload.assignmentGroup = basic.assignment.assignmentGroup;
  }

  if (basic.assignment?.escalatedTo) {
    payload.escalationTo = basic.assignment.escalatedTo;
    payload.escalationToName = basic.assignment.esName;
    payload.escalationToRole = basic.assignment.role;
    payload.assignTo = basic.assignment.escalatedTo;
    payload.assignToName = basic.assignment.esName;
    payload.assignToRole = basic.assignment.role;
  }
  return payload;
};

export const prepareEditApiPayload = (incident: Incident, user: any) => {
  const cleaned = cleanIncidentData(incident);
  console.log('Submitting full res:', cleaned);

  const basic = cleaned.basicInfo ?? {};

  const payload: any = {
    ticketNumber: basic.ticketNumber,
    description: basic.description,
    shortDescription: basic.shortDescription,
    rentalReferenceNumber: basic.agreementnumber,
    storeNumber: basic.storenumber,
    status: basic.status,
    requestType: basic.request_type,
    channel: basic.channel,
    category: basic.category,
    subCategory: basic.sub_category,
    actionType: basic.action_type,
    language: basic.language,
    contactInfo: {
      firstName: basic.firstName,
      lastName: basic.lastName,
      primaryPhoneNumber: unformatPhone(basic.callback),
      alternatePhoneNumber: unformatPhone(basic.alterphonenumb),
    },
    comments: cleaned.assignment.comment,
    attachments: Array.isArray(basic.attachments)
      ? basic.attachments
          .filter((attachment: any) => !attachment.attachmentId)
          .map((attachment: any) => ({
            actionType: attachment.actionType || 'ADD',
            documentName: attachment.attachmentName,
            documentLocation: attachment.attachmentLocation,
          }))
      : [],
  };

  if (cleaned.assignment?.assignmentGroup) {
    payload.assignmentGroup = cleaned.assignment.assignmentGroup;
  }

  if (cleaned.assignment?.escalatedTo) {
    payload.escalationTo = cleaned.assignment.escalatedTo;
    payload.escalationToName = cleaned.assignment.esName;
    payload.escalationToRole = cleaned.assignment.role;
    payload.assignTo = cleaned.assignment.escalatedTo;
    payload.assignToName = cleaned.assignment.esName;
    payload.assignToRole = cleaned.assignment.role;
  }

  return payload;
};

export const removeEmptyFields = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj
      .map(removeEmptyFields)
      .filter(
        (item) =>
          !(
            item === null ||
            item === undefined ||
            item === '' ||
            (typeof item === 'object' && Object.keys(item).length === 0)
          )
      );
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleanedObj: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = removeEmptyFields(value);
      if (
        cleanedValue !== null &&
        cleanedValue !== undefined &&
        cleanedValue !== '' &&
        !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
        !(
          typeof cleanedValue === 'object' &&
          Object.keys(cleanedValue).length === 0
        )
      ) {
        cleanedObj[key] = cleanedValue;
      }
    });
    return cleanedObj;
  }
  return obj;
};

export const formatPhone = (phone?: string | null): string => {
  const trimmed = phone?.trim();
  if (!trimmed) return '';

  let digits = trimmed.replace(/\D+/g, '');

  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : trimmed;
};

export const mapTicketToIncident = (ticketData: any): Incident => {
  console.log('ticketData', ticketData);

  const rcaNote =
    ticketData?.ticketNotes?.find((note: any) => note.noteType === 'RCA')
      ?.notes || '';

  return {
    basicInfo: {
      ticketId: ticketData.cmsTicketId,
      ticketNumber: ticketData.csmTicketNumber,
      channel: ticketData.channel,
      request_type: ticketData.requestType,
      category: ticketData.categoryType,
      firstName: ticketData.contactInfo?.firstName,
      sub_category: ticketData.subCategoryType,
      lastName: ticketData.contactInfo?.lastName,
      action_type: ticketData.actionType,
      callback: formatPhone(ticketData.contactInfo?.primaryPhoneNumber),
      agreementnumber: ticketData.rentalReferenceNumber,
      alterphonenumb: formatPhone(ticketData.contactInfo?.alternatePhoneNumber),
      storenumber: ticketData.storeNumber,
      description: ticketData.description,
      shortDescription: ticketData.shortDescription,
      language: ticketData.language,
      attachments: ticketData.attachments ?? [],
      status: ticketData.status,
    },
    assignment: {
      assignmentGroup: ticketData.assignmentgroup,
      esName: ticketData.escalateToName,
      escalatedTo: ticketData.escalateTo,
      role: ticketData.escalateToRole,
      assigned: !!ticketData.escalateTo,
      comment: ticketData.comments,
    },
    resolution: {
      resolved: !!ticketData.resolvedBy,
      rscode: ticketData.resolutionType ?? 'NONE',
      rsnote: rcaNote,
    },
    activity: {
      businessDuration: ticketData.businessDuration,
      activities: ticketData.activities,
      ticketNotes: ticketData.ticketNotes,
    },
  };
};

export const fetchData = async (
  ticketId: string,
  updateIncidentSection: <K extends keyof Incident>(
    section: K,
    data: Partial<Incident[K]>
  ) => void,
  setScreenLoader: (loading: boolean) => void,
  updateInitialTktInfo?: (data: any) => void
): Promise<{
  mappedIncident?: Incident;
  escalationOptions?: EscalationOption[];
}> => {
  try {
    const res = await getTicketInfo({ ticketId });
    if (!res) return {};
    const mappedIncident = mapTicketToIncident(res);

    updateIncidentSection('basicInfo', mappedIncident.basicInfo);
    updateIncidentSection('assignment', mappedIncident.assignment);
    updateIncidentSection('resolution', mappedIncident.resolution);
    updateIncidentSection('activity', mappedIncident.activity);

    if (updateInitialTktInfo) {
      updateInitialTktInfo(res);
    }

    const { storenumber: storeNumber } = mappedIncident.basicInfo || {};
    const {
      assignmentGroup,
      escalatedTo,
      role: escalateToRole,
    } = mappedIncident.assignment || {};

    const isEmpty = (val: any) =>
      val === undefined || val === null || val === '';

    const shouldFetchEscalation = !!storeNumber && isEmpty(assignmentGroup);
    const phoneNumber: string | null = null;

    if (shouldFetchEscalation) {
      try {
        const options = await fetchEscalatedData(storeNumber);
        if (options.length > 0) {
          const matchedOption = options.find(
            (opt: any) =>
              opt.value === String(escalatedTo) && opt.role === escalateToRole
          );

          const phoneNumber = matchedOption?.phone_number || null;
          updateIncidentSection('assignment', {
            escalationContact: phoneNumber ?? 'none',
          });
        }
        return { mappedIncident, escalationOptions: options };
      } catch (error) {
        console.error('[Escalation] Error fetching options:', error);
        return {
          mappedIncident,
          escalationOptions: [{ label: 'None', value: '' }],
        };
      }
    }

    return { mappedIncident };
  } catch (error) {
    console.error('Error submitting ticket:', error);
  } finally {
    setScreenLoader(false);
  }
  return {};
};

export const parseBusinessDuration = (
  duration: string
): {
  display: string;
} => {
  const safe = (duration ?? '00:00:00:00').trim();
  const parts = safe.split(':').map((v) => Number(v));
  const [dd = 0, hh = 0, mm = 0, ss = 0] =
    parts.length === 4 ? parts : [0, 0, 0, 0];

  const days = Number.isFinite(dd) && dd >= 0 ? dd : 0;
  const hours = Number.isFinite(hh) && hh >= 0 ? hh : 0;
  const minutes = Number.isFinite(mm) && mm >= 0 ? mm : 0;
  const seconds = Number.isFinite(ss) && ss >= 0 ? ss : 0;

  const plural = (n: number, unit: string) =>
    `${n} ${unit}${n === 1 ? '' : 's'}`;

  const partsOut: string[] = [];
  if (days) partsOut.push(plural(days, 'day'));
  if (hours) partsOut.push(`${hours} hr${hours === 1 ? '' : 's'}`);
  if (minutes) partsOut.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  if (!days && !hours && !minutes && seconds) {
    partsOut.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
  }
  const display = partsOut.join(' ') || '0 minutes';
  return {
    display,
  };
};
export const mapInitialInfo = (
  data: any,
  updateIncidentSection: <K extends keyof Incident>(
    section: K,
    data: Partial<Incident[K]>
  ) => void
) => {
  const mappedIncident = mapTicketToIncident(data);
  updateIncidentSection('basicInfo', mappedIncident.basicInfo);
  updateIncidentSection('assignment', mappedIncident.assignment);
  updateIncidentSection('resolution', mappedIncident.resolution);
  updateIncidentSection('activity', mappedIncident.activity);
};

export type ReferenceOption = {
  id?: string | number;
  value: string;
  label?: string;
  pid?: string | number; // parent id
  requiresStore?: boolean;
  meta?: { requiresStore?: boolean };
};

export const FORM_RULES_CONFIG = {
  /**
   * Put the exact request_type values that should require Store Number.
   * Example: ["Store", "Store Related"]
   */
  requiredStoreRequestTypes: [] as string[],
};

export const isValidReferenceKey = (key: string): key is ReferenceKeys => {
  return Object.values(ReferenceKeys as any).includes(key as any);
};

export const getCategoryIdByValue = (
  categoryOptions: ReferenceOption[] | undefined,
  value: string | undefined
) => categoryOptions?.find((o) => o.value === value)?.id;

export const getSubCategoryIdByValue = (
  subCategoryOptions: ReferenceOption[] | undefined,
  value: string | undefined
) => subCategoryOptions?.find((o) => o.value === value)?.id;

export const getFilteredSubcategories = (
  selectedCategoryId: string | number | undefined,
  subCategoryOptions?: ReferenceOption[]
) => {
  if (!Array.isArray(subCategoryOptions)) return [] as ReferenceOption[];
  if (!selectedCategoryId) return [] as ReferenceOption[];
  return subCategoryOptions.filter(
    (s) => s.pid?.toString() === selectedCategoryId?.toString()
  );
};

export const getFilteredActions = (
  selectedSubCategoryId: string | number | undefined,
  actionOptions?: ReferenceOption[]
) => {
  if (!Array.isArray(actionOptions)) return [] as ReferenceOption[];
  if (!selectedSubCategoryId) return [] as ReferenceOption[];
  return actionOptions.filter(
    (a) => a.pid?.toString() === selectedSubCategoryId?.toString()
  );
};
const STORE_RELAX_TYPES = new Set(['dsdeal', 'dscust']);

const isRelaxStoreType = (requestType?: string) => {
  if (!requestType) return false;
  return STORE_RELAX_TYPES.has(String(requestType).toLowerCase());
};
export const requiresStoreNumber = (
  requestTypeValue: string | undefined,
  requestTypeOptions?: ReferenceOption[]
): boolean => {
  if (!requestTypeValue) return false;
  const opt = requestTypeOptions?.find((o) => o.value === requestTypeValue);
  const flag = (opt as any)?.requiresStore ?? (opt as any)?.meta?.requiresStore;
  if (typeof flag === 'boolean') return flag;
  return FORM_RULES_CONFIG.requiredStoreRequestTypes.includes(requestTypeValue);
};

export const computeRequiredFields = (params: {
  baseRequiredFields: string[];
  basicInfo: any;
  references: Record<string, (ReferenceOption | Option)[]>;
  selectedCategoryId?: string | number;
  selectedSubCategoryId?: string | number;
  filteredSubCategoryOptions: Option[];
  filteredActionOptions: Option[];
}): string[] => {
  const {
    baseRequiredFields,
    basicInfo,
    selectedCategoryId,
    selectedSubCategoryId,
    filteredSubCategoryOptions,
    filteredActionOptions,
  } = params;

  const req = new Set(baseRequiredFields);

  // Default required
  req.add('sub_category');
  req.add('action_type');
  req.add('storenumber');

  if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
    if (filteredSubCategoryOptions.length === 0) {
      req.delete('sub_category');
      req.delete('action_type');
      // eslint-disable-next-line sonarjs/no-collapsible-if
    } else if (
      selectedSubCategoryId !== undefined &&
      selectedSubCategoryId !== null
    ) {
      if (filteredActionOptions.length === 0) {
        req.delete('action_type');
      }
    }
  }

  if (isRelaxStoreType(basicInfo?.request_type)) {
    req.delete('storenumber');
  } else {
    req.add('storenumber');
  }

  return Array.from(req);
};

export const validateRequiredFields = (
  reqFields: string[],
  basicInfo: Record<string, any>
): boolean => {
  return reqFields.every((field) => {
    const value = (basicInfo as any)?.[field];
    if (basicInfo.callback && basicInfo.callback.length < 14) {
      return false;
    }

    if (basicInfo.alterphonenumb && basicInfo.alterphonenumb.length < 14) {
      return false;
    }
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null;
  });
};

export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

export type ReadFileAsBase64 = (file: File) => Promise<string>;

export const uniqueifyFilenames = (files: File[], existingNames: string[]) => {
  const seen = new Set(existingNames.map((n) => n.toLowerCase()));
  const output: File[] = [];

  files.forEach((file) => {
    const name = file.name.replace(/\s+/g, '_');
    const dotIdx = name.lastIndexOf('.');
    const base = dotIdx >= 0 ? name.slice(0, dotIdx) : name;
    const ext = dotIdx >= 0 ? name.slice(dotIdx) : '';

    let finalName = name;
    let counter = 1;
    while (seen.has(finalName.toLowerCase())) {
      finalName = `${base}_${counter}${ext}`;
      counter += 1;
    }

    // We cannot actually rename a File object; keep original File but track the finalName in payload
    // We'll return a shadow object below with custom documentName
    Object.defineProperty(file, 'finalName', {
      value: finalName,
      writable: false,
    });
    seen.add(finalName.toLowerCase());
    output.push(file);
  });

  return output;
};

export const buildUploadPayload = async (
  files: File[],
  readFileAsBase64: ReadFileAsBase64
) => {
  const attachments: { documentBase64: string; documentName: string }[] = [];

  const tasks = files.map(async (file) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return null;
    }
    const fullBase64 = await readFileAsBase64(file);
    const base64 = fullBase64.split(',')[1];
    const cleanName: string =
      (file as any).finalName || file.name.replace(/\s+/g, '_');
    attachments.push({ documentBase64: base64, documentName: cleanName });
    return null;
  });

  await Promise.allSettled(tasks);

  return { attachments, type: 'upload' } as const;
};

export const decodeAndTriggerDownload = async (
  base64: string,
  fileName: string,
  fileType?: string
) => {
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    txt: 'text/plain',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
  };

  const typeKey = (fileType || '').toLowerCase();
  const mimeType = mimeTypes[typeKey] || 'application/octet-stream';

  const byteCharacters = atob(base64.trim());
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++)
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const noneOption: Option = {
  label: 'None',
  value: '',
  id: undefined,
  pid: undefined,
};

export const toOptions = (arr?: (ReferenceOption | Option)[]): Option[] => {
  if (!Array.isArray(arr)) return [noneOption];

  return arr.map((o: any) => ({
    label:
      typeof o.label === 'string' && o.label.trim()
        ? o.label
        : String(o.value ?? ''),
    value: String(o.value ?? ''),
    id: o.id,
    pid: o.pid,
  }));
};

export const fetchEscalatedData = async (
  storeNumber: string
): Promise<EscalationOption[]> => {
  try {
    const result = await fetchDMList({
      storeNumbers: [storeNumber],
      csmRequest: true,
    });
    const store = result.response?.[0];
    const dm = store?.districtManager?.[0];
    const rd = store?.regionalManager?.[0];

    const options: EscalationOption[] = [{ label: 'None', value: '' }];

    if (dm) {
      options.push({
        label: `DM - ${dm.app_user_first_name} ${dm.app_user_last_name}`,
        value: dm.employee_id.toString(),
        esName: `${dm.app_user_first_name} ${dm.app_user_last_name}`,
        phone_number: dm.phone_number,
        role: 'DM',
      });
    }

    if (rd) {
      options.push({
        label: `RD - ${rd.app_user_first_name} ${rd.app_user_last_name}`,
        value: rd.employee_id.toString(),
        esName: `${rd.app_user_first_name} ${rd.app_user_last_name}`,
        phone_number: rd.phone_number,
        role: 'RD',
      });
    }

    return options;
  } catch (error) {
    console.error('API call failed:', error);
    return [{ label: 'Select', value: '' }];
  }
};

const isEmpty = (val: unknown) => {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string') return val.trim().length === 0;
  if (Array.isArray(val)) return val.length === 0;
  return false;
};

export const isFieldMissing = (
  name: string,
  value: unknown,
  requiredFields: string[]
) => {
  const required = requiredFields.includes(name);
  return required && isEmpty(value);
};
